import * as React from 'react';
import {ControlButton, Glyphs, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import UserMessaging from '../../util/UserMessaging';
import {useTranslation} from 'react-i18next';
import {SetupWorkerState} from '../../model/workerState';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import logging from '../../service/logging';
import collectionApi from '../../service/collectionApi';
import {collectionsStore} from '../../context/collectionsProvider';
import {ICollectionModel} from '../../model/appModel';
import {BooleanParam, useQueryParam} from 'use-query-params';
import Constants, {GroupRoleTypes} from '../../constants/Constants';
import {AlertModalPortal} from '../util/ModalPortalHelper';

export const CollectionNotificationToggleBase: React.FC<IStateMappedProps> = ({activeCollection}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [isNotificationEnabled, setIsNotificationEnabled] = React.useState(null);
   const [loadingState, setLoadingState] = SetupWorkerState();
   // Grab the value of the query param 'unsubscribe' to enable unsubscribing from collections via a URL
   const [unsubscribeParam, setUnsubscribeParam] = useQueryParam(Constants.queryParams.unsubscribe, BooleanParam);
   const [showCollectionNotificationModal, setShowCollectionNotificationModal] = React.useState(false);

   const undoUnsubscribe = () => {
      void collectionApi.subscribeToCollection(activeCollection.Id);
      setIsNotificationEnabled(true);
      setShowCollectionNotificationModal(false);
   };

   const onCloseNotificationsModal = () => {
      setUnsubscribeParam(null);
      setShowCollectionNotificationModal(false);
   };

   React.useEffect(() => {
      if (isNotificationEnabled) {
         unsubscribe();
      } else if (isNotificationEnabled === false) {
         // Remove the query param 'unsubscribe' from the URL, preventing it from causing any side effects on navigation or page refresh
         setUnsubscribeParam(null);
      }
   }, [isNotificationEnabled]);

   const getNotificationPreference = async () => {
      if (activeCollection && activeCollection.Role !== GroupRoleTypes.notMember) {
         setIsNotificationEnabled(await collectionApi.isSubscribedToCollection(activeCollection.Id) !== false);
      }
   };

   const unsubscribe = () => {
      if (unsubscribeParam) {
         void collectionApi.unsubscribeFromCollection(activeCollection.Id);
         setIsNotificationEnabled(false);
         setShowCollectionNotificationModal(true);
      }
   };

   React.useEffect(() => {
      void getNotificationPreference();
   }, [activeCollection]);

   const toggleNotificationPreference = async (): Promise<void> => {
      if (!loadingState.isWorking) {
         try {
            setLoadingState({isWorking: true, hasError: false});
            const newNotificationValue = !isNotificationEnabled;
            setIsNotificationEnabled(newNotificationValue);
            if (newNotificationValue) {
               await collectionApi.subscribeToCollection(activeCollection.Id);
            } else {
               await collectionApi.unsubscribeFromCollection(activeCollection.Id);
            }
            setLoadingState({isWorking: false, hasError: false});
         } catch (e) {
            setLoadingState({isWorking: false, hasError: true});
            logging.error('Failed to toggle notification preference', e);
            UserMessaging.warn(t('collections.failedUpdatePreference'));
         }
      }
   };

   return (
      <>
         <ControlButton
            glyph={isNotificationEnabled ? <Glyphs.Alert16x16 /> : <Glyphs.AlertOutline16x16 />}
            buttonType={TscButtonType.tertiary}
            title={isNotificationEnabled ? t('collections.options.unsubscribeToCollectionAlerts') : t('collections.options.subscribeToCollectionAlerts')}
            label={t('collections.notify')}
            onClick={toggleNotificationPreference}
            active={isNotificationEnabled}
            themeName={theme}
            testId="collection-notification-toggle"
            disabled={loadingState.isWorking}
         />
         {showCollectionNotificationModal && (
            <AlertModalPortal
               title={t('collections.collectionNotifications')}
               onClose={onCloseNotificationsModal}
               onConfirm={onCloseNotificationsModal}
               confirmButtonType={TscButtonType.secondary}
               confirmContent={t('general.close')}
               onDismiss={undoUnsubscribe}
               dismissButtonType={TscButtonType.tertiary}
               dismissContent={t('collections.undoUnsubscribe')}
            >
               {t('collections.unsubscribeConfirmation')}
            </AlertModalPortal>
         )}
      </>
   );
};

export interface IStateMappedProps {
   activeCollection: ICollectionModel;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {activeCollection}: Partial<IStateMappedProps>
): IStateMappedProps => ({activeCollection});

export default withMemoizedContexts(mapStatesToProps, collectionsStore)(CollectionNotificationToggleBase);
